import React, { useEffect, useRef, useState } from "react";
import { RxCross2 } from "react-icons/rx";
import style from "./style.module.css";
import Button from "../button/Button";

// In-page signature capture: draw on a canvas (mouse/touch/pen, via Pointer
// Events) or upload a photo of a signature instead. Resolves to either
// { dataUrl } (canvas PNG) or { file } (uploaded image) via onSubmit — the
// caller (CreatorSignupDetails) posts whichever one it gets to the backend.
const SignaturePad = ({ onSubmit, onClose, submitting }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [mode, setMode] = useState("draw");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState(null);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#141414";
  }, []);

  useEffect(() => {
    // Revoke the object URL for the previous file preview when it's replaced/unmounted.
    return () => {
      if (uploadPreviewUrl) URL.revokeObjectURL(uploadPreviewUrl);
    };
  }, [uploadPreviewUrl]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawingRef.current = true;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const handlePointerMove = (e) => {
    if (!drawingRef.current) return;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasDrawn) setHasDrawn(true);
  };

  const handlePointerUp = () => {
    drawingRef.current = false;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    setUploadFile(file);
    setUploadPreviewUrl(URL.createObjectURL(file));
  };

  const switchMode = (next) => {
    setMode(next);
    if (next === "draw") {
      setUploadFile(null);
      setUploadPreviewUrl(null);
    } else {
      clearCanvas();
    }
  };

  const canSubmit = agreed && (mode === "draw" ? hasDrawn : !!uploadFile);

  const handleConfirm = () => {
    if (!canSubmit || submitting) return;
    if (mode === "draw") {
      onSubmit({ dataUrl: canvasRef.current.toDataURL("image/png") });
    } else {
      onSubmit({ file: uploadFile });
    }
  };

  return (
    <div className={style.reviewLightboxOverlay} onClick={onClose}>
      <div
        className={style.reviewLightboxBody}
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(92vw, 520px)", background: "#1e1e1e", borderRadius: 16, padding: 24 }}
      >
        <button type="button" className={style.reviewLightboxClose} onClick={onClose} aria-label="Close">
          <RxCross2 />
        </button>

        <h3 className={style.reviewDetailsTitle} style={{ marginBottom: 4 }}>
          Sign Document
        </h3>
        <p className={style.reviewDetailsLabel} style={{ marginBottom: 16 }}>
          Draw your signature below, or upload a photo of it.
        </p>

        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          <Button variant={mode === "draw" ? "dark" : "outline"} onClick={() => switchMode("draw")}>
            Draw
          </Button>
          <Button variant={mode === "upload" ? "dark" : "outline"} onClick={() => switchMode("upload")}>
            Upload image
          </Button>
        </div>

        {mode === "draw" ? (
          <>
            <canvas
              ref={canvasRef}
              width={472}
              height={180}
              style={{
                width: "100%",
                height: 180,
                background: "#fff",
                borderRadius: 10,
                touchAction: "none",
                cursor: "crosshair",
                display: "block",
              }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
            />
            <div style={{ marginTop: 8, marginBottom: 16 }}>
              <Button variant="outline" onClick={clearCanvas}>
                Clear
              </Button>
            </div>
          </>
        ) : (
          <div style={{ marginBottom: 16 }}>
            <input type="file" accept="image/*" onChange={handleFileChange} style={{ color: "#f0f0f0" }} />
            {uploadPreviewUrl && (
              <div style={{ marginTop: 12, background: "#fff", borderRadius: 10, padding: 12, display: "inline-block" }}>
                <img src={uploadPreviewUrl} alt="Signature preview" style={{ maxHeight: 120, maxWidth: "100%" }} />
              </div>
            )}
          </div>
        )}

        <label style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 18, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span className={style.reviewDetailsValue}>
            I agree that this image constitutes my legal signature on this agreement, to the extent permitted by
            applicable law.
          </span>
        </label>

        <Button onClick={handleConfirm} disabled={!canSubmit} loading={submitting}>
          Confirm Signature
        </Button>
      </div>
    </div>
  );
};

export default SignaturePad;
